import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import {
    Card,
    Row,
    Col,
    Input,
    Button,
    Table,
    Typography,
    Modal,
    List,
    Spin,
    Empty,
    Popover,
    Tag,
    Space,
    message,
    Tooltip,
} from 'antd';
import {
    HistoryOutlined,
    SearchOutlined,
    ReloadOutlined,
    CopyOutlined,
    DeleteOutlined,
    ArrowRightOutlined,
    CheckCircleOutlined,
    DashboardOutlined,
    LoadingOutlined,
    DesktopOutlined,
    MobileOutlined,
    QuestionCircleOutlined,
    ThunderboltOutlined,
    RiseOutlined,
    EditOutlined,
    PlusCircleOutlined,
    RocketOutlined,
} from '@ant-design/icons';
import AuthService from '../../services/authService';
import { dispatchQuotaUpdated } from '../../utils/quotaEvent';
import { useInlineCopy } from '../../hooks/useInlineCopy';
import ProtectedMenuPage from '../../components/ProtectedMenuPage/ProtectedMenuPage';
import KakaoFloatButton from '../../components/KakaoFloatButton/KakaoFloatButton';
import './KeyWordVer2.css';

const KeyWordVer2 = ({ title: _title = '키워드 추출순위 Ver2' }) => {
    const { inlineCopied, handleInlineCopy } = useInlineCopy(1200);

    // 메시지 알림 위치 상단 중앙으로 설정
    message.config({
        top: 80,
        duration: 2,
        maxCount: 3,
    });

    const [productId, setProductId] = useState('');
    const [productName, setProductName] = useState('');
    const [excludeKeywords, setExcludeKeywords] = useState('');
    const [includeKeyword, setIncludeKeyword] = useState('');
    const [rankings, setRankings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [rankLoading, setRankLoading] = useState(false);
    const [volumeLoading, setVolumeLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [_showUrlInput, _setShowUrlInput] = useState(true);
    const [productUrl, setProductUrl] = useState('');
    const [scrapedTags, setScrapedTags] = useState([]);
    const [selectedTagsOrder, setSelectedTagsOrder] = useState([]);
    const [scrapedMid, setScrapedMid] = useState('');
    const [analysisLoading, setAnalysisLoading] = useState(false);
    const [showAnalysisModal, setShowAnalysisModal] = useState(false);
    const [analysisReport, setAnalysisReport] = useState('');

    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [historyDataGubun, setHistoryDataGubun] = useState('keywordver2');

    const [historyData, setHistoryData] = useState([]);
    const [historyLoaded, setHistoryLoaded] = useState(false);
    const PAGE_SIZE = 10;
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    const productIdRef = useRef(null);
    const productNameRef = useRef(null);
    const excludeKeywordsRef = useRef(null);
    const includeKeywordRef = useRef(null);

    const handleUrlScrape = async () => {
        if (!productUrl.trim()) {
            message.warning('URL을 입력하세요.');
            return;
        }

        // URL 유효성 검증 (네이버 스마트스토어/브랜드스토어 상품번호 포함 확인)
        const isNaverStore =
            productUrl.includes('smartstore.naver.com') || productUrl.includes('brand.naver.com');
        if (isNaverStore && !/\/products\/\d+/.test(productUrl)) {
            message.error(
                '올바른 상품 URL을 입력해주세요. (URL 끝에 상품 번호가 포함되어야 합니다)'
            );
            return;
        }

        // 새로운 조회 시작 시 기존 상태 초기화
        setProductName('');
        setProductId('');
        setScrapedMid('');
        setScrapedTags([]);
        setSelectedTagsOrder([]);

        setLoading(true);
        try {
            const res = await AuthService.authAxios.post('/product/scrape-url', {
                url: productUrl,
            });
            if (res.data.success) {
                const { title, productId: mid, tags: _tags } = res.data.product;

                if (!mid) {
                    message.warning('상품 정보를 찾을 수 없습니다. URL을 다시 확인해주세요.');
                    return;
                }

                setProductName(title || '');
                setProductId(mid || '');
                setScrapedMid(mid || '');

                // 상품명을 공백으로 쪼개서 태그로 저장 (단어 중복 제거 및 길이 필터링)
                const words = title
                    ? title
                          .split(' ')
                          .map(w => w.trim().replace(/[^\w\s가-힣]/g, '')) // 특수문자 제거
                          .filter(w => w.length >= 2)
                    : [];
                setScrapedTags([...new Set(words)]);

                setSelectedTagsOrder([]); // 새로운 상품 조회 시 선택 순서 초기화
                message.success('상품 정보를 성공적으로 가져왔습니다.');
            } else {
                message.warning(res.data.message || '상품 정보를 가져오지 못했습니다.');
            }
        } catch (err) {
            console.error('URL 조회 실패:', err);
            message.error(err?.response?.data?.message || '상품 정보 조회 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const _handleAiAnalysis = async () => {
        if (rankings.length === 0) {
            message.warning('분석할 순위 데이터가 없습니다.');
            return;
        }

        setAnalysisLoading(true);
        setShowAnalysisModal(true);
        setAnalysisReport('');

        try {
            const res = await AuthService.authAxios.post('/product/analyze', {
                productName,
                rankings: rankings.map(r => ({
                    keyword: r.keyword,
                    rank: r.rank,
                    searchVolume: r.searchVolume,
                })),
            });

            if (res.data.success) {
                setAnalysisReport(res.data.report);
            } else {
                message.error(res.data.message || 'AI 분석에 실패했습니다.');
                setShowAnalysisModal(false);
            }
        } catch (err) {
            console.error('AI 분석 에러:', err);
            message.error('AI 분석 중 오류가 발생했습니다.');
            setShowAnalysisModal(false);
        } finally {
            setAnalysisLoading(false);
        }
    };

    const fetchRankings = async () => {
        if (!productId.trim()) {
            message.warning('MID를 입력하세요.');
            productIdRef.current.focus();
            return;
        }
        if (!/^\d+$/.test(productId.trim())) {
            message.warning('MID는 숫자 형식이어야 합니다.');
            productIdRef.current.focus();
            return;
        }
        if (!productName.trim()) {
            message.error('상품명을 입력하세요.');
            productNameRef.current.focus();
            return;
        }

        const msgKey = 'fetching_rankings_v2';
        message.config({
            top: 8,
            duration: 2,
            maxCount: 1,
        });
        message.loading({ content: '상품 최적화 키워드 추출 중...', key: msgKey, duration: 0 });

        setLoading(true);
        setSearched(true);
        setRankLoading(false);
        setVolumeLoading(false);

        try {
            // 1단계: 키워드 목록만 가볍게 먼저 가져옴
            const response = await AuthService.authAxios.get(`/productV2`, {
                params: {
                    productId,
                    keyword: includeKeyword,
                    productName,
                    excludeKeywords: excludeKeywords,
                    onlyKeywords: true,
                },
            });

            if (!response.data.success) {
                message.warning({ content: '상품 정보가 없습니다.', key: msgKey, duration: 3 });
                setLoading(false);
                return;
            }

            const initialRankings = response.data.rankings;
            setRankings(initialRankings);
            setLoading(false); // 키워드 목록 나오면 메인 로딩 종료

            const keywords = initialRankings.map(r => r.keyword);
            const chunkSize = 5;

            // 2단계: 순위 비동기 업데이트
            setRankLoading(true);
            try {
                let currentRankStep = 0;
                message.loading({
                    content: `키워드 순위 조회 중... (0%)`,
                    key: msgKey,
                    duration: 0,
                });

                for (let i = 0; i < keywords.length; i += chunkSize) {
                    const chunk = keywords.slice(i, i + chunkSize);
                    const rankRes = await AuthService.authAxios.post(`/product/v2/rankings`, {
                        productId,
                        keywords: chunk,
                    });

                    if (rankRes.data.success) {
                        const newRanks = rankRes.data.rankings;
                        setRankings(prev =>
                            prev.map(item => {
                                const match = newRanks.find(r => r.keyword === item.keyword);
                                return match ? { ...item, rank: match.rank } : item;
                            })
                        );
                    }
                    currentRankStep += chunk.length;
                    const percent = Math.min(
                        100,
                        Math.round((currentRankStep / keywords.length) * 100)
                    );
                    message.loading({
                        content: `키워드 순위 조회 중... (${percent}%)`,
                        key: msgKey,
                        duration: 0,
                    });
                }
            } catch (rankErr) {
                console.error('순위 조회 실패:', rankErr);
            } finally {
                setRankLoading(false);
            }

            // 3단계: 검색량 비동기 업데이트
            setVolumeLoading(true);
            try {
                let currentVolStep = 0;
                message.loading({
                    content: `키워드 검색량 조회 중... (0%)`,
                    key: msgKey,
                    duration: 0,
                });

                for (let i = 0; i < keywords.length; i += chunkSize) {
                    const chunk = keywords.slice(i, i + chunkSize);
                    const volRes = await AuthService.authAxios.post(`/product/v2/volumes`, {
                        keywords: chunk,
                    });

                    if (volRes.data.success) {
                        const volumes = volRes.data.volumes;
                        setRankings(prev =>
                            prev.map(item => {
                                const match = volumes.find(v => v.keyword === item.keyword);
                                return match ? { ...item, searchVolume: match.searchVolume } : item;
                            })
                        );
                    }
                    currentVolStep += chunk.length;
                    const percent = Math.min(
                        100,
                        Math.round((currentVolStep / keywords.length) * 100)
                    );
                    message.loading({
                        content: `키워드 검색량 조회 중... (${percent}%)`,
                        key: msgKey,
                        duration: 0,
                    });
                }
            } catch (volErr) {
                console.error('검색량 조회 실패:', volErr);
            } finally {
                setVolumeLoading(false);
            }

            // 최종 성공 메시지
            message.success({ content: '조회 완료', key: msgKey, duration: 2 });

            // 최종 쿼터 업데이트
            try {
                const finalRankStatus = await AuthService.authAxios.get(
                    '/naver/quota/status/keywordVer2'
                );
                if (finalRankStatus.data?.ok) {
                    dispatchQuotaUpdated('keywordVer2');
                }
            } catch (qErr) {
                console.error('쿼터 업데이트 실패:', qErr);
            }
        } catch (err) {
            console.error(err);
            message.error({
                content: err?.response?.data?.message || '조회 중 오류가 발생했습니다.',
                key: msgKey,
                duration: 3,
            });
            setLoading(false);
        }
    };

    const handleReset = () => {
        setProductId('');
        setProductName('');
        setExcludeKeywords('');
        setIncludeKeyword('');
        setRankings([]);
        setSearched(false);
        if (productIdRef.current) productIdRef.current.focus();
    };

    const handleDelete = async id => {
        Modal.confirm({
            title: '정말 삭제하시겠습니까?',
            content: '삭제된 기록은 되돌릴 수 없습니다.',
            okText: '삭제',
            okType: 'danger',
            cancelText: '취소',
            onOk: async () => {
                try {
                    await AuthService.authAxios.post('/log/delete', { id });
                    setHistoryData(prev => prev.filter(item => item.id !== id));
                    message.success('성공적으로 삭제되었습니다.');
                } catch (err) {
                    console.error('삭제 실패:', err);
                    message.error('삭제에 실패했습니다.');
                }
            },
        });
    };

    const handleApplyInput = inputString => {
        const values = Object.fromEntries(inputString.split('|').map(pair => pair.split('=')));
        if (historyDataGubun === 'keywordver2') {
            setProductId(values.productId || '');
            setProductName(values.productName || '');
            setExcludeKeywords(values.excludeKeywords || '');
            setIncludeKeyword(values.keyword || '');
        } else {
            setProductName(values.product_name || '');
            setProductId(values.syncNvMid || '');
        }
        setShowHistoryModal(false);
    };

    const fetchHistory = async (nextOffset = 0, append = false, gubun = 'keywordver2') => {
        setHistoryDataGubun(gubun);
        setHistoryLoaded(false);

        try {
            const res = await AuthService.authAxios.get('/log/history', {
                params: {
                    gubun: gubun,
                    offset: nextOffset,
                },
            });

            if (res.data.success) {
                const newData = res.data.data;
                if (!append) {
                    setHasMore(true);
                }
                if (newData.length < PAGE_SIZE) setHasMore(false);

                if (append) {
                    setHistoryData(prev => [...prev, ...newData]);
                    setOffset(prev => prev + PAGE_SIZE);
                } else {
                    setHistoryData(newData);
                    setOffset(PAGE_SIZE);
                }
            }
        } catch (err) {
            console.error('조회 기록 불러오기 실패:', err);
        } finally {
            setHistoryLoaded(true);
        }
    };

    const { Text } = Typography;

    const copyToClipboard = (text, type = 'all') => {
        navigator.clipboard.writeText(text);
        if (type === 'all') {
            message.success(`${rankings.length}개 키워드가 복사되었습니다.`);
        }
    };

    const columns = [
        {
            title: (
                <Space>
                    키워드
                    <Button
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => copyToClipboard(rankings.map(r => r.keyword).join('\n'))}
                    >
                        전체 복사
                    </Button>
                </Space>
            ),
            dataIndex: 'keyword',
            key: 'keyword',
            render: (text, record, index) => {
                const id = `${text}|${index}`;
                const isCopied = inlineCopied?.group === 'ver2' && inlineCopied?.id === id;
                return (
                    <div
                        onClick={() => handleInlineCopy('ver2', id, text)}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleInlineCopy('ver2', id, text);
                            }
                        }}
                        role="button"
                        tabIndex={0}
                        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    >
                        <Text strong>{text}</Text>
                        {isCopied && (
                            <Tag
                                color="blue"
                                style={{ marginLeft: 8 }}
                                icon={<CheckCircleOutlined />}
                            >
                                복사됨
                            </Tag>
                        )}
                    </div>
                );
            },
        },
        {
            title: (
                <Tooltip title="네이버 쇼핑 기준 상품 순위">
                    <span>순위</span>
                </Tooltip>
            ),
            showSorterTooltip: { title: '순위로 정렬' },
            dataIndex: 'rank',
            key: 'rank',
            align: 'center',
            sorter: (a, b) => (a.rank || 9999) - (b.rank || 9999),
            render: rank => {
                if (rank === null) {
                    return rankLoading ? (
                        <span style={{ color: '#1890ff', fontSize: '12px' }}>
                            <LoadingOutlined style={{ marginRight: 4 }} /> 조회중...
                        </span>
                    ) : (
                        '-'
                    );
                }
                return (
                    <Tag color={rank === 0 ? 'default' : rank <= 10 ? 'red' : 'blue'}>
                        {rank === 0 ? '확인 불가' : rank}
                    </Tag>
                );
            },
        },
        {
            title: (
                <Tooltip title="최근 30일간 PC에서 검색된 횟수">
                    <Space>
                        <DesktopOutlined />
                        <span>PC 검색량</span>
                    </Space>
                </Tooltip>
            ),
            showSorterTooltip: { title: 'PC 검색량으로 정렬' },
            dataIndex: ['searchVolume', 'pc'],
            key: 'pc',
            align: 'right',
            sorter: (a, b) => (a.searchVolume?.pc || 0) - (b.searchVolume?.pc || 0),
            render: (val, _record) => {
                if (val === undefined || val === null) {
                    return volumeLoading ? (
                        <span style={{ color: '#1890ff', fontSize: '12px' }}>
                            <LoadingOutlined style={{ marginRight: 4 }} /> 조회중...
                        </span>
                    ) : (
                        '-'
                    );
                }
                if (typeof val === 'number') return val.toLocaleString();
                return val; // '< 10' 등 문자열 그대로 반환
            },
        },
        {
            title: (
                <Tooltip title="최근 30일간 모바일에서 검색된 횟수">
                    <Space>
                        <MobileOutlined />
                        <span>모바일 검색량</span>
                    </Space>
                </Tooltip>
            ),
            showSorterTooltip: { title: '모바일 검색량으로 정렬' },
            dataIndex: ['searchVolume', 'mobile'],
            key: 'mobile',
            align: 'right',
            sorter: (a, b) => {
                const valA = typeof a.searchVolume?.mobile === 'number' ? a.searchVolume.mobile : 0;
                const valB = typeof b.searchVolume?.mobile === 'number' ? b.searchVolume.mobile : 0;
                return valA - valB;
            },
            render: (val, _record) => {
                if (val === undefined || val === null) {
                    return volumeLoading ? (
                        <span style={{ color: '#1890ff', fontSize: '12px' }}>
                            <LoadingOutlined style={{ marginRight: 4 }} /> 조회중...
                        </span>
                    ) : (
                        '-'
                    );
                }
                if (typeof val === 'number') return val.toLocaleString();
                return val; // '< 10' 등 문자열 그대로 반환
            },
        },
    ];

    const handleSubmit = e => {
        e?.preventDefault?.();
        if (loading) return;
        fetchRankings();
    };

    return (
        <ProtectedMenuPage menuKey="keywordVer2">
            <div className="keyword-ver2-page">
                {/* 상단: 검색 영역 - 가로 배치 (KeyWordVer1 스타일) */}
                <div className="page-header-container no-header-border">
                    <div style={{ marginBottom: '10px' }}>
                        <Card
                            className="control-card keyword-ver2-search-card"
                            bodyStyle={{ padding: '8px 16px' }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <Input
                                    placeholder="상품 URL을 입력하세요 (예: 스마트스토어)"
                                    value={productUrl}
                                    onChange={e => setProductUrl(e.target.value)}
                                    style={{ flex: 1, fontSize: 12 }}
                                    onPressEnter={() => message.info('URL 분석 기능 준비 중...')}
                                />
                                <Button
                                    type="primary"
                                    icon={<SearchOutlined />}
                                    onClick={handleUrlScrape}
                                    loading={loading}
                                    style={{ flexShrink: 0 }}
                                >
                                    URL 상품 자동 조회
                                </Button>
                            </div>
                            {scrapedTags.length > 0 && (
                                <div
                                    style={{
                                        marginTop: '12px',
                                        borderTop: '1px dashed #f0f0f0',
                                        paddingTop: '12px',
                                    }}
                                >
                                    <Space
                                        direction="vertical"
                                        style={{ width: '100%' }}
                                        size="small"
                                    >
                                        <Space wrap align="center" style={{ marginBottom: '8px' }}>
                                            <Text
                                                type="secondary"
                                                style={{ fontSize: '13px', fontWeight: 500 }}
                                            >
                                                상품명 단성(선택):
                                            </Text>
                                            {scrapedTags.map((tag, idx) => {
                                                const isSelected = selectedTagsOrder.includes(tag);
                                                return (
                                                    <Tag
                                                        key={idx}
                                                        color={isSelected ? 'blue' : 'default'}
                                                        style={{
                                                            cursor: 'pointer',
                                                            borderRadius: '4px',
                                                            padding: '4px 10px',
                                                            border: isSelected
                                                                ? '1px solid #1890ff'
                                                                : '1px solid #d9d9d9',
                                                            fontWeight: isSelected
                                                                ? '600'
                                                                : 'normal',
                                                            backgroundColor: isSelected
                                                                ? '#e6f7ff'
                                                                : '#fff',
                                                            transition: 'all 0.3s',
                                                        }}
                                                        onClick={() => {
                                                            const currentIncludeTags =
                                                                includeKeyword
                                                                    .split(',')
                                                                    .map(v => v.trim())
                                                                    .filter(Boolean);

                                                            if (isSelected) {
                                                                setSelectedTagsOrder(prev =>
                                                                    prev.filter(t => t !== tag)
                                                                );
                                                                setIncludeKeyword(
                                                                    currentIncludeTags
                                                                        .filter(t => t !== tag)
                                                                        .join(', ')
                                                                );
                                                            } else {
                                                                let nextOrder = [
                                                                    ...selectedTagsOrder,
                                                                ];
                                                                let nextInclude = [
                                                                    ...currentIncludeTags,
                                                                ];

                                                                if (nextOrder.length >= 2) {
                                                                    const oldestTag =
                                                                        nextOrder.shift();
                                                                    nextInclude =
                                                                        nextInclude.filter(
                                                                            t => t !== oldestTag
                                                                        );
                                                                }

                                                                nextOrder.push(tag);
                                                                if (!nextInclude.includes(tag)) {
                                                                    nextInclude.push(tag);
                                                                }

                                                                setSelectedTagsOrder(nextOrder);
                                                                setIncludeKeyword(
                                                                    nextInclude.join(', ')
                                                                );
                                                            }
                                                        }}
                                                    >
                                                        {tag}
                                                    </Tag>
                                                );
                                            })}
                                        </Space>

                                        {scrapedMid && (
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <Text
                                                    type="secondary"
                                                    style={{ fontSize: '13px', fontWeight: 500 }}
                                                >
                                                    조회된 MID:
                                                </Text>
                                                <Text
                                                    copyable={{ text: scrapedMid }}
                                                    style={{
                                                        marginLeft: '8px',
                                                        color: '#08979c',
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    {scrapedMid}
                                                </Text>
                                            </div>
                                        )}
                                    </Space>
                                </div>
                            )}
                        </Card>
                    </div>
                    <Card
                        className="control-card keyword-ver2-search-card"
                        bodyStyle={{ padding: '8px 16px' }}
                    >
                        <form onSubmit={handleSubmit}>
                            <Row
                                gutter={[16, 16]}
                                align="middle"
                                className="keyword-ver2-search-row"
                            >
                                <Col
                                    xs={24}
                                    sm={24}
                                    md={24}
                                    flex="0 0 auto"
                                    className="keyword-ver2-inputs-col"
                                >
                                    <div className="keyword-ver2-inputs-group">
                                        <div className="keyword-ver2-input-block">
                                            <label
                                                htmlFor="productId-input"
                                                className="keyword-ver2-label"
                                            >
                                                <span style={{ color: 'red', marginRight: 4 }}>
                                                    *
                                                </span>
                                                단일 MID
                                            </label>
                                            <Input
                                                id="productId-input"
                                                size="small"
                                                placeholder="MID 입력"
                                                value={productId}
                                                onChange={e => setProductId(e.target.value)}
                                                ref={productIdRef}
                                                required
                                                className="keyword-ver2-input keyword-ver2-input-narrow"
                                            />
                                        </div>
                                        <div className="keyword-ver2-input-block">
                                            <label
                                                htmlFor="productName-input"
                                                className="keyword-ver2-label"
                                            >
                                                <span style={{ color: 'red', marginRight: 4 }}>
                                                    *
                                                </span>
                                                상품명
                                            </label>
                                            <Input
                                                id="productName-input"
                                                size="small"
                                                placeholder="상품명 입력"
                                                value={productName}
                                                onChange={e => setProductName(e.target.value)}
                                                ref={productNameRef}
                                                required
                                                className="keyword-ver2-input"
                                            />
                                        </div>
                                        <div className="keyword-ver2-input-block">
                                            <label
                                                htmlFor="includeKeyword-input"
                                                className="keyword-ver2-label"
                                            >
                                                포함 키워드 (선택)
                                            </label>
                                            <Popover
                                                content={
                                                    <div
                                                        style={{
                                                            maxWidth: '240px',
                                                            fontSize: '13px',
                                                            lineHeight: '1.6',
                                                        }}
                                                    >
                                                        <div>쉼표로 구분, 상품명에 있는 단어</div>
                                                        <div
                                                            style={{
                                                                marginTop: '4px',
                                                                color: '#666',
                                                            }}
                                                        >
                                                            예) 포함 키워드 : &apos;한우&apos;
                                                        </div>
                                                    </div>
                                                }
                                                trigger="hover"
                                                placement="top"
                                                mouseEnterDelay={0.15}
                                                mouseLeaveDelay={0.1}
                                            >
                                                <QuestionCircleOutlined className="keyword-ver2-tooltip-icon" />
                                            </Popover>
                                            <Input
                                                id="includeKeyword-input"
                                                size="large"
                                                placeholder="예) 포함 키워드 : '한우'" // eslint-disable-line react/no-unescaped-entities
                                                value={includeKeyword}
                                                onChange={e => setIncludeKeyword(e.target.value)}
                                                ref={includeKeywordRef}
                                                className="keyword-ver2-input keyword-ver2-input-narrow"
                                            />
                                        </div>
                                        <div className="keyword-ver2-input-block">
                                            <label
                                                htmlFor="excludeKeywords-input"
                                                className="keyword-ver2-label"
                                            >
                                                제외 키워드
                                            </label>
                                            <Popover
                                                content={
                                                    <div
                                                        style={{
                                                            maxWidth: '280px',
                                                            fontSize: '13px',
                                                            lineHeight: '1.6',
                                                        }}
                                                    >
                                                        <div>쉼표로 구분</div>
                                                        <div
                                                            style={{
                                                                marginTop: '4px',
                                                                color: '#666',
                                                            }}
                                                        >
                                                            예) 제외키워드 : &apos;1.8kg&apos;,
                                                            &apos;상견례&apos;, &apos;최고급형&apos;
                                                        </div>
                                                    </div>
                                                }
                                                trigger="hover"
                                                placement="top"
                                                mouseEnterDelay={0.15}
                                                mouseLeaveDelay={0.1}
                                            >
                                                <QuestionCircleOutlined className="keyword-ver2-tooltip-icon" />
                                            </Popover>
                                            <Input
                                                id="excludeKeywords-input"
                                                size="small"
                                                placeholder="예) '1.8kg', '상견례', '최고급형'" // eslint-disable-line react/no-unescaped-entities
                                                value={excludeKeywords}
                                                onChange={e => setExcludeKeywords(e.target.value)}
                                                ref={excludeKeywordsRef}
                                                className="keyword-ver2-input keyword-ver2-input-narrow"
                                            />
                                        </div>
                                    </div>
                                </Col>
                                <Col
                                    xs={24}
                                    sm={24}
                                    md={24}
                                    flex="1 1 auto"
                                    className="keyword-ver2-buttons-col"
                                >
                                    <Space
                                        size="small"
                                        wrap={false}
                                        className="keyword-ver2-buttons"
                                    >
                                        <Button
                                            type="primary"
                                            className="search-btn-premium"
                                            icon={<SearchOutlined />}
                                            onClick={handleSubmit}
                                            loading={loading}
                                        >
                                            조회하기
                                        </Button>
                                        <Button
                                            className="page-action-btn"
                                            icon={<ReloadOutlined />}
                                            onClick={handleReset}
                                        >
                                            초기화
                                        </Button>
                                        <Button
                                            className="history-btn"
                                            icon={<HistoryOutlined />}
                                            onClick={() => {
                                                setShowHistoryModal(true);
                                                fetchHistory(0, false, 'keywordver2');
                                            }}
                                        >
                                            기록
                                        </Button>
                                        <Button
                                            className="history-btn"
                                            icon={<DashboardOutlined />}
                                            onClick={() => {
                                                setShowHistoryModal(true);
                                                fetchHistory(0, false, 'findmid');
                                            }}
                                        >
                                            MID기록
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                        </form>
                    </Card>
                </div>

                {/* 하단: 결과 영역 */}
                <div className="page-content-inner">
                    {!searched ? (
                        <Card
                            bodyStyle={{
                                padding: '48px 24px',
                                textAlign: 'center',
                                background: '#fafafa',
                                borderRadius: '12px',
                            }}
                        >
                            <SearchOutlined
                                style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }}
                            />
                            <Typography.Paragraph
                                type="secondary"
                                style={{ margin: 0, fontSize: '15px' }}
                            >
                                단일 MID, 상품명, 포함 키워드를 입력한 후{' '}
                                <Text strong>조회하기</Text> 버튼을 클릭하세요.
                            </Typography.Paragraph>
                        </Card>
                    ) : loading ? (
                        <div style={{ textAlign: 'center', padding: '48px' }}>
                            <Spin size="large" />
                        </div>
                    ) : (
                        <Card
                            className="keyword-ver2-result-card"
                            title="순위 조회 결과"
                            /* extra={
                                <Button 
                                    type="primary" 
                                    icon={<DashboardOutlined />} 
                                    onClick={handleAiAnalysis}
                                    style={{ backgroundColor: '#722ed1', borderColor: '#722ed1', borderRadius: '6px' }}
                                >
                                    AI 상품 분석
                                </Button>
                            } */
                            bodyStyle={{ padding: 0 }}
                        >
                            <Table
                                columns={columns}
                                dataSource={rankings}
                                rowKey={(record, index) => `${record.keyword}|${index}`}
                                pagination={false}
                                locale={{
                                    emptyText: <Empty description="조회된 데이터가 없습니다." />,
                                }}
                            />
                        </Card>
                    )}
                </div>
            </div>

            <Modal
                title={historyDataGubun === 'keywordver2' ? '조회 기록' : 'MID 기록'}
                open={showHistoryModal}
                onCancel={() => setShowHistoryModal(false)}
                centered
                mask={{ blur: true }}
                footer={[
                    <Button key="close" onClick={() => setShowHistoryModal(false)}>
                        닫기
                    </Button>,
                ]}
                width={800}
                className="history-modal"
                bodyStyle={{ maxHeight: '600px', overflowY: 'auto' }}
            >
                {!historyLoaded ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                    </div>
                ) : historyData.length === 0 ? (
                    <Empty description="조회 기록이 없습니다." />
                ) : (
                    <List
                        dataSource={historyData}
                        renderItem={item => {
                            const parts = {};
                            item.input.split('|').forEach(pair => {
                                const [key, value] = pair.split('=');
                                parts[key] = value;
                            });

                            return (
                                <List.Item
                                    actions={[
                                        <Button
                                            key="apply"
                                            type="primary"
                                            size="small"
                                            icon={<ArrowRightOutlined />}
                                            onClick={() => {
                                                handleApplyInput(item.input);
                                                setShowHistoryModal(false);
                                            }}
                                        >
                                            {historyDataGubun === 'keywordver2'
                                                ? '적용'
                                                : 'MID 적용'}
                                        </Button>,
                                        <Button
                                            key="delete"
                                            danger
                                            size="small"
                                            icon={<DeleteOutlined />}
                                            onClick={() => handleDelete(item.id)}
                                        >
                                            삭제
                                        </Button>,
                                    ]}
                                >
                                    <List.Item.Meta
                                        title={
                                            historyDataGubun === 'keywordver2' ? (
                                                <Space wrap>
                                                    <Text strong>
                                                        상품명: {parts.productName || '-'}
                                                    </Text>
                                                    <Text type="secondary">
                                                        MID: {parts.productId || '-'} / 포함:{' '}
                                                        {parts.keyword || '-'} / 제외:{' '}
                                                        {parts.excludeKeywords || '-'}
                                                    </Text>
                                                </Space>
                                            ) : (
                                                <div>
                                                    <Text strong>
                                                        스토어: {parts.channel_name || '-'}
                                                    </Text>
                                                    <br />
                                                    <Text
                                                        type="secondary"
                                                        style={{ fontSize: '13px' }}
                                                    >
                                                        상품명: {parts.product_name || '-'}
                                                    </Text>
                                                </div>
                                            )
                                        }
                                        description={
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                {item.updated_at || item.searched_at}
                                            </Text>
                                        }
                                    />
                                </List.Item>
                            );
                        }}
                    >
                        {hasMore && historyData.length > 0 && (
                            <div style={{ textAlign: 'center', marginTop: '16px' }}>
                                <Button
                                    onClick={() => fetchHistory(offset, true, historyDataGubun)}
                                >
                                    더보기
                                </Button>
                            </div>
                        )}
                    </List>
                )}
            </Modal>
            <Modal
                title={
                    <Space>
                        <DashboardOutlined style={{ color: '#722ed1' }} />
                        <span>AI 상품 분석 보고서</span>
                    </Space>
                }
                open={showAnalysisModal}
                onCancel={() => setShowAnalysisModal(false)}
                footer={[
                    <Button
                        key="close"
                        type="primary"
                        onClick={() => setShowAnalysisModal(false)}
                        style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
                    >
                        닫기
                    </Button>,
                ]}
                width={800}
                centered
                mask={{ blur: true }}
            >
                {analysisLoading ? (
                    <div style={{ textAlign: 'center', padding: '100px 0' }}>
                        <Spin
                            tip="네이버 검색 엔진 알고리즘 데이터를 분석하고 전문가 리포트를 생성 중입니다..."
                            size="large"
                        />
                    </div>
                ) : (
                    <div style={{ maxHeight: '750px', overflowY: 'auto', padding: '8px' }}>
                        {/* 리포트 헤더 추가 */}
                        <div
                            style={{
                                textAlign: 'center',
                                marginBottom: '40px',
                                padding: '20px 0',
                                borderBottom: '2px double #f0f0f0',
                            }}
                        >
                            <div
                                style={{
                                    color: '#722ed1',
                                    fontWeight: '800',
                                    fontSize: '24px',
                                    marginBottom: '8px',
                                }}
                            >
                                NAVER SHOPPING SEO 분석 보고서
                            </div>
                            <div style={{ color: '#8c8c8c', fontSize: '14px' }}>
                                분석 상품: <Text strong>{productName}</Text> | 일시:{' '}
                                {new Date().toLocaleString()}
                            </div>
                        </div>

                        {analysisReport ? (
                            analysisReport.split('■').map((section, idx) => {
                                if (!section.trim()) return null;
                                const lines = section.trim().split('\n');
                                const header = lines[0].replace(/[*#]/g, '').trim();
                                const body = lines
                                    .slice(1)
                                    .join('\n')
                                    .replace(/[*#]/g, '')
                                    .replace(/---/g, '')
                                    .trim();

                                // 본문이 비어있으면 렌더링하지 않음
                                if (!body && !header.includes('점')) return null;

                                // 섹션별 아이콘 정의
                                const icons = [
                                    <CheckCircleOutlined key="icon1" />,
                                    <SearchOutlined key="icon2" />,
                                    <ThunderboltOutlined key="icon3" />,
                                    <RiseOutlined key="icon4" />,
                                    <EditOutlined key="icon5" />,
                                    <PlusCircleOutlined key="icon6" />,
                                    <RocketOutlined key="icon7" />,
                                ];
                                const currentIcon = icons[(idx - 1) % icons.length] || (
                                    <CheckCircleOutlined key="default" />
                                );

                                return (
                                    <div
                                        key={idx}
                                        style={{
                                            marginBottom: '32px',
                                            borderRadius: '16px',
                                            overflow: 'hidden',
                                            border: '1px solid #f0f0f0',
                                            boxShadow: '0 8px 24px rgba(114, 46, 209, 0.04)',
                                            backgroundColor: '#fff',
                                        }}
                                    >
                                        <div
                                            style={{
                                                backgroundColor: '#fdfaff',
                                                padding: '16px 24px',
                                                borderBottom: '1px solid #f3e8ff',
                                                color: '#722ed1',
                                                fontWeight: '700',
                                                fontSize: '17px',
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <span style={{ marginRight: '12px', fontSize: '20px' }}>
                                                {currentIcon}
                                            </span>
                                            {header}
                                        </div>
                                        <div
                                            style={{
                                                whiteSpace: 'pre-wrap',
                                                lineHeight: '1.9',
                                                fontSize: '15px',
                                                color: '#262626',
                                                padding: '24px 32px',
                                            }}
                                        >
                                            {/* 첫 번째 섹션(점수)에 특별한 스타일 적용 */}
                                            {idx === 1 &&
                                            (body.includes('점') || header.includes('점')) ? (
                                                <div
                                                    style={{
                                                        backgroundColor: '#fffdf6',
                                                        padding: '24px',
                                                        borderRadius: '12px',
                                                        border: '1px solid #fff1b8',
                                                        color: '#d46b08',
                                                        fontWeight: '700',
                                                        marginBottom: '8px',
                                                        textAlign: 'center',
                                                        fontSize: '22px',
                                                    }}
                                                >
                                                    {header.includes('점')
                                                        ? header.split(':')[1] || header
                                                        : body.split('\n')[0]}
                                                    <div
                                                        style={{
                                                            fontSize: '15px',
                                                            fontWeight: 'normal',
                                                            color: '#8c8c8c',
                                                            marginTop: '10px',
                                                            borderTop: '1px solid #fff1b8',
                                                            paddingTop: '10px',
                                                        }}
                                                    >
                                                        {header.includes('점')
                                                            ? body
                                                            : body.split('\n').slice(1).join('\n')}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div style={{ padding: '0 8px' }}>
                                                    {body.trim()}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div style={{ textAlign: 'center', color: '#bfbfbf', padding: '40px' }}>
                                분석 데이터를 생성하지 못했습니다.
                            </div>
                        )}

                        {/* 리포트 푸터 (부드러운 디자인) */}
                        <div
                            style={{
                                marginTop: '40px',
                                padding: '20px 24px',
                                backgroundColor: '#e6f7ff',
                                borderRadius: '16px',
                                border: '1px solid #bae7ff',
                                color: '#0050b3',
                                fontSize: '14px',
                                lineHeight: '1.7',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginBottom: '8px',
                                    fontWeight: '700',
                                    fontSize: '15px',
                                }}
                            >
                                <span style={{ marginRight: '8px' }}>💡</span> 이용 안내
                            </div>
                            본 성과 리포트는 AI 알고리즘의 분석 데이터로 실제 상위 노출 및 매출
                            지표를 확약하지 않습니다. 검색 엔진의 정책 및 경쟁 상황에 따라 수치는
                            유동적으로 변할 수 있으므로, 최종 적용 시 판매자의 신중한 판단이
                            필수적입니다.
                        </div>
                    </div>
                )}
            </Modal>
            <KakaoFloatButton />
        </ProtectedMenuPage>
    );
};

export { KeyWordVer2 };
KeyWordVer2.propTypes = {
    title: PropTypes.string,
};
